/**
 * Barcode Print Utilities — Xprinter XP-TT426B BarTender hitman.btw template
 *
 * Physical sticker spec (exact, from the BarTender template):
 *   - Sheet width      : 75 mm
 *   - Section size     : 75 mm × 24 mm
 *   - Section gap      : 3 mm (non-printable feed)
 *   - Label size       : 25 mm × 12 mm
 *   - 5 labels per section, "brick" layout:
 *         TL (0,0)         TR (50,0)
 *               C (25,6)
 *         BL (0,12)        BR (50,12)
 *   - Center label sits in the middle column (X=25–50, Y=6) — it does NOT
 *     overlap top or bottom rows since they occupy outer columns only.
 *   - Section total height = label_h × 2 = 24 mm.
 *
 * All math in millimetres; printer is 203 DPI so mm-accurate CSS yields
 * the cleanest barcodes a handheld scanner can read.
 *
 * Exports:
 *   PAGE_WIDTH_MM, DEFAULT_CALIBRATION, mergeCalibration,
 *   expandLabels, chunkIntoSections, formatCurrency, normalizeProduct,
 *   calculateSectionHeightMm, calculatePartialSectionHeightMm,
 *   calculatePrintHeight, getSectionPositions
 */

export const PAGE_WIDTH_MM = 75;

// Canonical defaults — keep in sync with backend BARCODE_DEFAULTS.
export const DEFAULT_CALIBRATION = Object.freeze({
  // Position
  h_offset_mm: 0,
  v_offset_mm: 0,
  scale_pct: 100,
  // Label geometry (BarTender template)
  label_w_mm: 25,
  label_h_mm: 12,
  // Gap between sections (vertical, mm)
  section_gap_mm: 3,
  // Barcode
  barcode_w_scale: 100,
  barcode_h_mm: 6,
  // Text
  top_text_pt: 6,
  bottom_text_pt: 6,
  top_text_y_mm: 0.4,
  barcode_y_mm: 3,
  bottom_text_y_mm: 9.5,
  // Print
  bottom_margin_mm: 1.5,
});

/**
 * Merge user-supplied calibration onto defaults. Coerces nullish/empty values
 * back to default. Returns a fresh object.
 */
export function mergeCalibration(partial) {
  const out = { ...DEFAULT_CALIBRATION };
  if (!partial || typeof partial !== "object") return out;
  for (const k of Object.keys(DEFAULT_CALIBRATION)) {
    const v = partial[k];
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return out;
}

/** Expand products into one entry per copy (quantity). */
export function expandLabels(products) {
  const out = [];
  if (!Array.isArray(products)) return out;
  for (const p of products) {
    const qty = parseInt(p?.quantity, 10);
    if (!qty || qty <= 0) continue;
    const { quantity, ...rest } = p; // eslint-disable-line no-unused-vars
    for (let i = 0; i < qty; i++) out.push({ ...rest });
  }
  return out;
}

/** Chunk a flat array into fixed-size sections (default 5). */
export function chunkIntoSections(labels, perSection = 5) {
  if (!Array.isArray(labels) || labels.length === 0) return [];
  const size = Math.max(1, perSection);
  const out = [];
  for (let i = 0; i < labels.length; i += size) out.push(labels.slice(i, i + size));
  return out;
}

/** Format numeric values for label display. */
export function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}

/** Normalize PO line / Stock row / Product into canonical label input. */
export function normalizeProduct(raw, qty) {
  if (!raw || typeof raw !== "object") return null;
  const snap = raw.product_snapshot || {};
  const merged = { ...snap, ...raw };
  const product_id =
    merged.product_id || merged.id || merged.productId || merged.batch_product_id || "";
  const product_name = merged.product_name || merged.product || merged.name || "";
  const auto_barcode =
    merged.auto_barcode || merged.barcode || merged.manual_barcode || "";
  const mrp = merged.mrp ?? null;
  const default_price =
    merged.default_price ??
    merged.selling_price ??
    merged.last_purchase_cost ??
    merged.purchase_price ??
    merged.mrp ??
    null;
  return {
    product_id,
    product_name,
    auto_barcode,
    mrp,
    default_price,
    quantity: Math.max(1, parseInt(qty ?? 1, 10) || 1),
  };
}

/** Height of one FULL section (2 × label_h — the brick pattern). */
export function calculateSectionHeightMm(settings) {
  const s = mergeCalibration(settings);
  return 2 * s.label_h_mm;
}

/**
 * Height (mm) used by a PARTIAL section containing 1..4 labels.
 * Maps the highest used Y-position to physical height:
 *   1–2 labels (TL/TR only)            → bottom = label_h
 *   3 labels (+ Center at y=label_h/2) → bottom = 1.5 × label_h
 *   4–5 labels (+ Bottom row at y=label_h) → bottom = 2 × label_h
 */
export function calculatePartialSectionHeightMm(labelsInSection, settings) {
  const s = mergeCalibration(settings);
  const n = Math.max(0, Math.min(5, labelsInSection));
  if (n === 0) return 0;
  if (n <= 2) return s.label_h_mm;        // only top row used
  if (n === 3) return 1.5 * s.label_h_mm; // top row + center
  return 2 * s.label_h_mm;                // bottom row reached
}

/**
 * Compute exact print height (mm) for `totalLabels` labels.
 *
 * sections = ceil(N/5); last section may be partial.
 *   height = v_offset
 *          + (fullSections × full_h)
 *          + (fullSections × section_gap)      ← gap follows each full section IF a partial follows
 *          − (partial == 0 ? section_gap : 0)  ← trailing gap collapse when last section is full
 *          + partial_h
 *          + bottom_margin
 *
 * Cleaner form:
 *   sections = ceil(N/5)
 *   non-last full sections contribute full_h + section_gap each
 *   last section contributes full_h (if 5 labels) or partial_h (otherwise)
 */
export function calculatePrintHeight(totalLabels, settings) {
  if (!totalLabels || totalLabels <= 0) return 20;
  const s = mergeCalibration(settings);
  const fullSectionH = calculateSectionHeightMm(s);

  const sections = Math.ceil(totalLabels / 5);
  const lastLabelsCount = totalLabels - 5 * (sections - 1); // 1..5
  const lastSectionH =
    lastLabelsCount === 5
      ? fullSectionH
      : calculatePartialSectionHeightMm(lastLabelsCount, s);

  // (sections - 1) full sections precede the last; each contributes full_h + section_gap.
  const precedingH = (sections - 1) * (fullSectionH + s.section_gap_mm);

  const baseH =
    Math.max(0, s.v_offset_mm) + precedingH + lastSectionH + s.bottom_margin_mm;

  // Apply overall scale to the @page height too so the feed matches scaled output.
  const scale = Math.max(0.5, Math.min(1.5, (s.scale_pct || 100) / 100));
  return Math.max(10, baseH * scale);
}

/**
 * Slot positions for the 5 labels within a single section (mm), origin top-left
 * of the section box (75mm × 24mm by default).
 *
 *   TL (x=0,          y=0)
 *   TR (x=50,         y=0)
 *   C  (x=25,         y=label_h/2)        ← centered horizontally
 *   BL (x=0,          y=label_h)
 *   BR (x=50,         y=label_h)
 *
 * All x positions are derived from PAGE_WIDTH_MM and label_w_mm so the layout
 * scales naturally if the user adjusts label_w_mm.
 *
 *   leftCol  = h_offset
 *   rightCol = PAGE_WIDTH − label_w + h_offset
 *   centerX  = (PAGE_WIDTH − label_w) / 2 + h_offset
 *
 * Returns array of length 5: [TL, TR, C, BL, BR]
 */
export function getSectionPositions(settings) {
  const s = mergeCalibration(settings);
  const leftX = 0 + s.h_offset_mm;
  const rightX = PAGE_WIDTH_MM - s.label_w_mm + s.h_offset_mm;
  const centerX = (PAGE_WIDTH_MM - s.label_w_mm) / 2 + s.h_offset_mm;
  const centerY = s.label_h_mm / 2;
  const bottomY = s.label_h_mm;
  return [
    { left: leftX,   top: 0 },        // TL
    { left: rightX,  top: 0 },        // TR
    { left: centerX, top: centerY },  // C
    { left: leftX,   top: bottomY },  // BL
    { left: rightX,  top: bottomY },  // BR
  ];
}
